import { Injectable, Injector, inject, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { Observable, Subject, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { ThemeService } from './theme.service';
import { NotificationsService } from './notifications.service';
import { SubscriptionService } from './subscription.service';
import { ConnectionService } from './connection.service';

/**
 * Gestisce il ciclo di vita della sessione lato client: rinnovo dell'access
 * token tramite il refresh token (cookie httpOnly) e logout.
 *
 * Coordinamento: se più richieste scadono insieme (401), parte una sola
 * chiamata a /refresh; le altre aspettano il suo esito invece di lanciarne
 * altre in parallelo.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);
  private translate = inject(TranslateService);
  private themeService = inject(ThemeService);
  private injector = inject(Injector);

  private refreshing = false;
  private refreshDone$ = new Subject<string | null>();

  /**
   * Salva la sessione (token, preferenze) a valle di login classico o Google
   * e ritorna la route su cui reindirizzare, così i chiamanti (login,
   * registrazione) non duplicano questa logica.
   */
  applyLoginResult(result: any, returnUrl?: string | null): string {
    this.resetUserState();
    localStorage.setItem('token', result.access_token);
    localStorage.setItem('onboarding_completed', result.onboarding_completed ? 'true' : 'false');
    localStorage.setItem('profile_completed', result.profile_completed ? 'true' : 'false');
    localStorage.setItem('user_role', result.role ?? '');
    this.injector.get(NotificationsService).fetch();

    if (result.preferred_lang) {
      localStorage.setItem('lang', result.preferred_lang);
      this.translate.use(result.preferred_lang);
    }
    if (result.tours_completed?.length) {
      result.tours_completed.forEach((k: string) => localStorage.setItem(k, 'done'));
    }
    if (result.changelog_version) {
      localStorage.setItem('changelog_version', result.changelog_version);
    }
    if (result.theme) {
      this.themeService.applyTheme(result.theme);
      localStorage.setItem('theme', result.theme);
    }

    if (result.role === 'team_member') return '/team/matrimoni';
    if (!result.onboarding_completed) return '/onboarding';
    if (returnUrl && this.isSafeReturnUrl(returnUrl)) return returnUrl;
    return '/home';
  }

  /** Evita open redirect: solo path interni ("/xyz"), mai URL assoluti o protocol-relative ("//evil.com"). */
  private isSafeReturnUrl(url: string): boolean {
    return url.startsWith('/') && !url.startsWith('//');
  }

  refreshAccessToken(): Observable<string> {
    // Un refresh è già in corso → aspetta il suo esito.
    if (this.refreshing) {
      return this.refreshDone$.pipe(
        take(1),
        switchMap((token) =>
          token ? of(token) : throwError(() => new Error('refresh_failed')),
        ),
      );
    }

    this.refreshing = true;
    return this.http
      .post<{ access_token: string }>(
        `${environment.apiUrl}/auth/refresh`,
        {},
        { withCredentials: true },
      )
      .pipe(
        map((res) => res.access_token),
        tap((token) => {
          if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem('token', token);
          }
          this.refreshing = false;
          this.refreshDone$.next(token);
        }),
        catchError((err) => {
          this.refreshing = false;
          this.refreshDone$.next(null);
          return throwError(() => err);
        }),
      );
  }

  /** Revoca la tessera lato server. Best-effort: non blocca il logout. */
  logout(): Observable<any> {
    this.resetUserState();
    return this.http
      .post(`${environment.apiUrl}/auth/logout`, {}, { withCredentials: true })
      .pipe(catchError(() => of(null)));
  }

  clearSession(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('token');
    }
    this.resetUserState();
  }

  /**
   * Svuota i dati dell'utente tenuti in memoria dai servizi condivisi: senza,
   * chi accede subito dopo nella stessa scheda vedrebbe quelli del precedente.
   * Resolve lazy per non creare un ciclo di dipendenze con gli interceptor.
   */
  resetUserState(): void {
    this.injector.get(NotificationsService).reset();
    this.injector.get(SubscriptionService).reset();
    this.injector.get(ConnectionService).reset();
  }
}
